/**
 * Section Morph Adapter (factory) v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Generalized factory for "section morph on event" adapters. Cart is the first
 * consumer — future engines (`<account-engine>`, metafield-update sync, etc.)
 * reuse the same primitive.
 *
 * MENTAL MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * The morph itself (Alpine.morph + Section Rendering API) is already universal
 * and lives in `section-renderer.js`. What was cart-specific in
 * `cart-section-morph.js` was just two pieces:
 *
 *   1. A registry of section ids that should be re-rendered on a given event
 *   2. A listener on that event that hands `event.detail.sections` to morph
 *
 * Both are domain-agnostic — they only care about the event name and the
 * marker attribute on DOM elements. This factory captures both, parameterized.
 *
 * USAGE
 * ─────────────────────────────────────────────────────────────────────────────
 *   const adapter = window.Theme.createMorphAdapter({
 *     eventName:        window.Theme.events.NAMES.cartUpdate,  // 'cart:update'
 *     sectionAttribute: 'data-cart-section',
 *   });
 *
 *   adapter.register('my-section-id');
 *   adapter.unregister('my-section-id');
 *   adapter.getIds();  // ['cart-drawer', ...]
 *
 * Auto-discovery: every `[sectionAttribute]` element found at boot has its
 * closest `.shopify-section` ancestor registered (id stripped of the
 * `shopify-section-` prefix). New surfaces added after boot must call
 * `adapter.register(id)` explicitly — see the dev-mode mutation warning below.
 *
 * The adapter wires its own `document.addEventListener(eventName, ...)`. When
 * the event fires, it forwards `event.detail.sections` to
 * `window.Theme.morphSections`. The factory does NOT take a callback — keeping
 * registration symmetric: caller declares "what" (sections), factory handles
 * "how" (morph).
 *
 * LOAD ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * IIFE, defer-loaded after `events.js` (so `Theme.events.NAMES` is available
 * if any caller wires it during the same defer phase) and after
 * `section-renderer.js` exposure of `Theme.morphSections` (an ES module loaded
 * later — the listener resolves lazily at dispatch time, so no race).
 *
 * The cart adapter is instantiated INSIDE `<cart-engine>` (see
 * `assets/cart-engine.js`'s `connectedCallback`). Tests that need to inspect
 * the cart adapter registry should reach it via `window.cartEngine.sectionAdapter`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  window.Theme = window.Theme || {};

  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // createMorphAdapter — the factory
  // ─────────────────────────────────────────────────────────────────────────────

  function createMorphAdapter({ eventName, sectionAttribute } = {}) {
    if (typeof eventName !== 'string' || !eventName) {
      throw new Error('[section-morph-adapter] eventName is required');
    }
    if (typeof sectionAttribute !== 'string' || !sectionAttribute) {
      throw new Error('[section-morph-adapter] sectionAttribute is required');
    }

    const log = Logger.ns(`morph-adapter:${eventName}`);
    const registry = new Set();

    function register(sectionId) {
      if (typeof sectionId === 'string' && sectionId) {
        registry.add(sectionId);
      }
    }

    function unregister(sectionId) {
      if (typeof sectionId === 'string' && sectionId) {
        registry.delete(sectionId);
      }
    }

    function getIds() {
      return Array.from(registry);
    }

    function discover(root = document) {
      root.querySelectorAll(`[${sectionAttribute}]`).forEach((el) => {
        const sectionEl = el.closest('.shopify-section');
        if (!sectionEl || !sectionEl.id) return;
        const id = sectionEl.id.replace('shopify-section-', '');
        if (id) register(id);
      });
    }

    // Boot-time auto-discovery. If DOM is still loading, wait — otherwise scan
    // immediately so the registry is ready before the first event dispatches.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => discover(), { once: true });
    } else {
      discover();
    }

    // Event listener — forwards sections payload to the universal morph utility.
    // We resolve `morphSections` at dispatch time (not at factory call time) so
    // the section-renderer ES module can load after this IIFE without breaking.
    document.addEventListener(eventName, (event) => {
      const sections = event?.detail?.sections;
      if (!sections || typeof sections !== 'object') return;
      const morphSections = window.Theme && window.Theme.morphSections;
      if (typeof morphSections !== 'function') {
        log.warn('Theme.morphSections not available yet — section-renderer.js missing?');
        return;
      }
      morphSections(sections);
    });

    // Dev-mode mutation warning. Only active under Shopify's design-mode (theme
    // editor) — preserves the helpful hint from the old cart-section-morph
    // adapter while keeping the production path noise-free.
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== 'childList') return;
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (!node.hasAttribute || !node.hasAttribute(sectionAttribute)) return;
            const sectionEl = node.closest && node.closest('.shopify-section');
            if (!sectionEl || !sectionEl.id) return;
            const id = sectionEl.id.replace('shopify-section-', '');
            if (id && !registry.has(id)) {
              // eslint-disable-next-line no-console
              console.warn(
                `[morph-adapter:${eventName}] Section "${id}" added with [${sectionAttribute}] but not registered. ` +
                  `Call adapter.register("${id}") if it should receive morph updates.`,
              );
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return { register, unregister, getIds };
  }

  window.Theme.createMorphAdapter = createMorphAdapter;

  // The cart adapter is instantiated by <cart-engine> on connect (Slice 4 of
  // the cart-engine architecture plan). Consumers that previously read
  // `window.Theme.cartSectionAdapter` should reach it via
  // `window.cartEngine.sectionAdapter` instead.
})();
